import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getTrainingStatus, startTrainingFight } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { displayCardBS, formatTrainingRewardHint } from "@/constants/game.constants"
import { ELEMENT_NAMES, STYLE_ICONS } from "@/constants/guardians"
import { BattleCardView } from "@/types/battle"
import { gameCardToBattleCardView } from "@/utils/battleCards"
import { hasSelectedLeague } from "@/utils/onboardingPassport"
import AbilityPicker from "@/features/duel/components/AbilityPicker"
import { mapTrainingResult } from "@/features/training/mapTrainingResult"
import { resolveCardHeroImage } from "@/constants/cardAssets"
import RevealSequence from "@/features/duel/components/RevealSequence"
import CardPicker from "@/features/duel/components/CardPicker"

type Phase = "lobby" | "fighting" | "revealing" | "result"

export default function TrainingPage() {
  const {
    passport,
    collectionCards,
    applyPassport,
    syncGameState,
    bumpCards,
    refreshPassport,
  } = useGuardiansPassport()

  const [phase, setPhase] = useState<Phase>("lobby")
  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mappedResult, setMappedResult] = useState<ReturnType<
    typeof mapTrainingResult
  > | null>(null)

  const statusQuery = useQuery({
    queryKey: ["training-status"],
    queryFn: getTrainingStatus,
    enabled: phase === "lobby",
  })

  const battleCards = useMemo(
    () =>
      collectionCards
        .filter((c) => c.id.length > 0)
        .map(gameCardToBattleCardView),
    [collectionCards]
  )

  const guardianRank =
    passport?.guardianRankInfo?.rank ?? passport?.guardianRank ?? 1

  const status = statusQuery.data

  const progress =
    status && status.dailyRewardLimit > 0
      ? Math.min(1, status.trainingBattlesToday / status.dailyRewardLimit)
      : 0

  const startFight = useCallback(
    async (card: BattleCardView, abilityId?: string | null) => {
      if (!passport || phase !== "lobby") return
      if (!hasSelectedLeague(passport)) {
        setError("Сначала выберите лигу в Академии")
        return
      }
      setError(null)
      setPhase("fighting")
      try {
        const result = await startTrainingFight(card.id, abilityId)
        if (result.coinsDelta > 0) {
          applyPassport({
            ...passport,
            coins: passport.coins + result.coinsDelta,
          })
        }
        setMappedResult(mapTrainingResult(result, passport, card))
        setPhase("revealing")
      } catch (e: unknown) {
        setPhase("lobby")
        setError(getGameApiErrorMessage(e, "Не удалось начать тренировку"))
      }
    },
    [
      passport,
      phase,
      applyPassport,
      refreshPassport,
      bumpCards,
      statusQuery,
      syncGameState,
    ]
  )

  const resetToLobby = () => {
    setMappedResult(null)
    setSelectedAbilityId(null)
    setPhase("lobby")
    void statusQuery.refetch()
  }

  if (!passport) {
    return (
      <GameScreenLayout title="Тренировка" backTo="/play">
        <p className="text-sm text-guardians-muted">Игровой профиль не создан</p>
      </GameScreenLayout>
    )
  }

  if (phase === "revealing" && mappedResult) {
    return (
      <GameScreenLayout title="Тренировка" backTo="/play">
        <RevealSequence
          yourSide={mappedResult.yourSide}
          opponentSide={mappedResult.opponentSide}
          onComplete={() => {
            void refreshPassport().then(() => bumpCards())
            setPhase("result")
            void statusQuery.refetch()
            void syncGameState()
          }}
        />
      </GameScreenLayout>
    )
  }

  if (phase === "result" && mappedResult) {
    const outcome = mappedResult.yourSide.outcome
    return (
      <GameScreenLayout title="Тренировка" backTo="/play">
        <div
          className={clsx(
            "rounded-xl border p-4 text-center text-sm",
            outcome === "WIN" && "border-green-500/40",
            outcome === "LOSE" && "border-red-500/30",
            outcome === "DRAW" && "border-white/15"
          )}
        >
          <p className="text-xl font-bold text-guardians-accent">
            {outcome === "WIN"
              ? "Победа!"
              : outcome === "LOSE"
                ? "Поражение"
                : "Ничья"}
          </p>
          <p className="mt-2 text-guardians-muted">
            vs {mappedResult.opponentSide.player.nickname}
          </p>
          {mappedResult.yourSide.coinsDelta > 0 ? (
            <p className="mt-2 text-guardians-accent">
              +{mappedResult.yourSide.coinsDelta} монет
            </p>
          ) : null}
          <button
            type="button"
            onClick={resetToLobby}
            className="mt-4 w-full rounded-xl bg-guardians-accent py-2 text-guardians-bg"
          >
            Ещё бой
          </button>
        </div>
      </GameScreenLayout>
    )
  }

  return (
    <GameScreenLayout title="Тренировка" backTo="/play">
      {statusQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : status ? (
        <>
          <dl className="mb-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-guardians-muted">Бои сегодня</dt>
              <dd>
                {status.trainingBattlesToday}/{status.dailyRewardLimit}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-guardians-muted">Наград осталось</dt>
              <dd>{status.remainingRewardedBattles}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-guardians-muted">Монет за победу</dt>
              <dd>{status.coinsPerWin}</dd>
            </div>
          </dl>
          <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-guardians-accent transition-all"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="mb-4 text-xs text-guardians-muted">
            {formatTrainingRewardHint(
              status.remainingRewardedBattles,
              status.coinsPerWin
            )}
          </p>
        </>
      ) : null}

      {phase === "fighting" ? (
        <p className="text-sm text-guardians-accent">Бой с тенью…</p>
      ) : (
        <>
          <AbilityPicker
            affiliationElement={passport.affiliationElement}
            guardianRank={guardianRank}
            selectedAbilityId={selectedAbilityId}
            onSelect={setSelectedAbilityId}
            disabled={phase !== "lobby"}
          />

          {battleCards.length === 0 ? (
            <p className="text-sm text-amber-200">Нет карт в коллекции.</p>
          ) : (
            <div className="mt-3 max-h-80 overflow-y-auto">
              <CardPicker
                cards={battleCards}
                disabled={phase !== "lobby"}
                onSelect={(card) => void startFight(card, selectedAbilityId)}
              />
            </div>
          )}
        </>
      )}

      {error ? <p className="mt-3 text-sm text-red-300">{error}</p> : null}
    </GameScreenLayout>
  )
}
